import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';

interface LanguageContextType {
  changeLanguage: (locale: string) => Promise<void>;
  currentLanguage: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const { user, refreshUser } = useAuth();

  // Sincronizar idioma con el locale del usuario
  useEffect(() => {
    if (user?.locale) {
      i18n.changeLanguage(user.locale);
      localStorage.setItem('i18nextLng', user.locale);
    }
  }, [user?.locale, i18n]);

  const changeLanguage = async (locale: string) => {
    try {
      // Cambiar idioma en i18n
      await i18n.changeLanguage(locale);
      localStorage.setItem('i18nextLng', locale);
      
      // Actualizar locale del usuario en el backend si está autenticado
      if (user) {
        await apiService.updateProfile({ locale });
        await refreshUser();
      }
    } catch (error) {
      logger.error('Error al cambiar idioma:', error);
    }
  };

  const value: LanguageContextType = {
    changeLanguage,
    currentLanguage: i18n.language,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage debe ser usado dentro de LanguageProvider');
  }
  return context;
};

